import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import { MdMoreVert, MdRemoveRedEye, MdAttachMoney, MdLocalShipping, MdFilterList, MdDirectionsCar } from 'react-icons/md';
import { actions as toastrActions } from 'react-redux-toastr';
import Paper from 'components/Paper';
import { LoadingPage } from 'components/Loading';
import Images from 'assets/img';
import Button from 'components/Button';
import { embarcadoresAPI, fretesAPI } from 'services/apiRest';
import ModalDefinirValor from './components/ModalDefinirValor';
import ModalAssociarMotorista from './components/ModalAssociarMotorista';
import ModalGerarViagens from './components/ModalGerarViagens';
import {
  Container,
  Table,
  Th,
  Td,
  Tr,
  HeaderRow,
} from './styles';

const formatCpfCnpj = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value || '';
};

const getEmbarcadorLabel = (e) => {
  if (!e) return '';
  const nome = e.nome || e.nome_fantasia || e.nomeFantasia || 'Sem nome';
  const doc = e.cpfCnpj || e.cpf_cnpj || e.cnpj || e.cpf;
  const docFmt = formatCpfCnpj(doc);
  return docFmt ? `${nome} - ${docFmt}` : nome;
};

const getStatusLabel = (status) => {
  const statusMap = {
    aguardando_cotacao: 'Aguardando Cotação',
    aguardando_aprovacao: 'Aguardando Aprovação',
    buscando_motorista: 'Buscando Motorista',
    aguardando_carregamento: 'Aguardando Carregamento',
    emitindo_documentos: 'Emitindo Documentos',
    viagem_iniciada: 'Viagem Iniciada',
    viagem_encerrada: 'Viagem Encerrada',
    cancelado: 'Cancelado',
  };
  return statusMap[status] || (status ? status : 'Não Publicado');
};

const getStatusColor = (status) => {
  const colorMap = {
    aguardando_cotacao: '#ff9800',
    aguardando_aprovacao: '#ffc107',
    buscando_motorista: '#2196f3',
    aguardando_carregamento: '#9c27b0',
    emitindo_documentos: '#673ab7',
    viagem_iniciada: '#4caf50',
    viagem_encerrada: '#607d8b',
    cancelado: '#f44336',
  };
  return colorMap[status] || (status ? '#666' : '#999');
};

export default function PedidosFrete() {
  const history = useHistory();
  const dispatch = useDispatch();
  const [fretes, setFretes] = useState([]);
  const [embarcadores, setEmbarcadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuFreteId, setMenuFreteId] = useState(null);
  const [modalValorOpen, setModalValorOpen] = useState(false);
  const [modalMotoristaOpen, setModalMotoristaOpen] = useState(false);
  const [modalGerarViagensOpen, setModalGerarViagensOpen] = useState(false);
  const [freteSelecionado, setFreteSelecionado] = useState(null);
  const [gerandoViagens, setGerandoViagens] = useState(false);

  // Paginação
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filtros
  const [filtros, setFiltros] = useState({
    status: '',
    embarcador_id: '',
    cidade_coleta: '',
    cidade_entrega: '',
    com_valor: '',
    data_inicio: '',
    data_fim: '',
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const embarcadorPorId = React.useMemo(() => {
    const map = new Map();
    (embarcadores || []).forEach((e) => map.set(e.id, e));
    return map;
  }, [embarcadores]);

  const carregar = async () => {
    setLoading(true);
    try {
      const [embarcadoresData] = await Promise.all([
        embarcadoresAPI.listar(),
      ]);
      setEmbarcadores(Array.isArray(embarcadoresData) ? embarcadoresData : []);

      // Carregar fretes com paginação e filtros
      await carregarFretes();
    } catch (e) {
      console.error('[PedidosFrete] Erro ao carregar:', e);
      setFretes([]);
      setEmbarcadores([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarFretes = async () => {
    try {
      const fretesData = await fretesAPI.listar();
      let fretesFiltrados = Array.isArray(fretesData) ? fretesData : [];

      // Aplicar filtros
      if (filtros.status) {
        fretesFiltrados = fretesFiltrados.filter(f => f.status === filtros.status);
      }
      if (filtros.embarcador_id) {
        fretesFiltrados = fretesFiltrados.filter(f => f.embarcador_id === filtros.embarcador_id);
      }
      if (filtros.cidade_coleta) {
        const termo = filtros.cidade_coleta.toLowerCase();
        fretesFiltrados = fretesFiltrados.filter(f =>
          (f.collect_city || '').toLowerCase().includes(termo)
        );
      }
      if (filtros.cidade_entrega) {
        const termo = filtros.cidade_entrega.toLowerCase();
        fretesFiltrados = fretesFiltrados.filter(f =>
          (f.delivery_city || '').toLowerCase().includes(termo)
        );
      }
      if (filtros.com_valor === 'sim') {
        fretesFiltrados = fretesFiltrados.filter(f => f.valor_frete && f.valor_frete > 0);
      } else if (filtros.com_valor === 'nao') {
        fretesFiltrados = fretesFiltrados.filter(f => !f.valor_frete || f.valor_frete <= 0);
      }

      setTotalItems(fretesFiltrados.length);

      // Aplicar paginação
      const inicio = (page - 1) * itemsPerPage;
      const fim = inicio + itemsPerPage;
      const fretesPaginados = fretesFiltrados.slice(inicio, fim);

      setFretes(fretesPaginados);
    } catch (e) {
      console.error('[PedidosFrete] Erro ao carregar fretes:', e);
      setFretes([]);
    }
  };

  const handleLimparFiltros = () => {
    setFiltros({
      status: '',
      embarcador_id: '',
      cidade_coleta: '',
      cidade_entrega: '',
      com_valor: '',
      data_inicio: '',
      data_fim: '',
    });
    setPage(1);
    // Executa a ação de limpar automaticamente
    setTimeout(() => {
      carregarFretes();
    }, 100);
  };

  const handleAplicarFiltros = () => {
    setPage(1);
    carregarFretes();
  };

  const toggleFiltros = () => {
    setMostrarFiltros(!mostrarFiltros);
  };

  const handleMenuOpen = (event, freteId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuFreteId(freteId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuFreteId(null);
  };

  const handleAction = (action, freteId) => {
    const frete = fretes.find((f) => f.id === freteId);
    if (!frete) return;

    switch (action) {
      case 'view':
        history.push(`/embarcadores/fretes/view/${freteId}`);
        break;
      case 'definir-valor':
        setFreteSelecionado(frete);
        setModalValorOpen(true);
        break;
      case 'associar-motorista':
        // Validação: não pode associar motorista sem valor
        if (!frete.valor_frete || frete.valor_frete <= 0) {
          dispatch(
            toastrActions.add({
              type: 'error',
              title: 'Erro',
              message: 'Não é possível associar motorista sem o valor do frete definido. Por favor, defina o valor primeiro.',
            })
          );
          handleMenuClose();
          return;
        }
        setFreteSelecionado(frete);
        setModalMotoristaOpen(true);
        break;
      case 'gerar-viagens':
        setFreteSelecionado(frete);
        setModalGerarViagensOpen(true);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const handleDefinirValor = async (valor) => {
    if (!freteSelecionado) return;
    try {
      await fretesAPI.atualizar(freteSelecionado.id, { valor_frete: valor });
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'Valor do frete definido com sucesso!',
        })
      );
      setModalValorOpen(false);
      setFreteSelecionado(null);
      await carregar();
    } catch (e) {
      console.error('[PedidosFrete] Erro ao definir valor:', e);
      throw new Error(e.response?.data?.detalhes || e.message || 'Erro ao definir valor.');
    }
  };

  const handleAssociarMotorista = async (motoristaId) => {
    if (!freteSelecionado) return;
    try {
      await fretesAPI.atualizar(freteSelecionado.id, { motorista_id: motoristaId });
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'Motorista associado com sucesso!',
        })
      );
      setModalMotoristaOpen(false);
      setFreteSelecionado(null);
      await carregar();
    } catch (e) {
      console.error('[PedidosFrete] Erro ao associar motorista:', e);
      throw new Error(e.response?.data?.detalhes || e.message || 'Erro ao associar motorista.');
    }
  };

  const handleGerarViagens = async () => {
    if (!freteSelecionado) return;
    setGerandoViagens(true);
    try {
      // Buscar XMLs do frete para mostrar quantos serão criados
      const xmls = await fretesAPI.listarXmls(freteSelecionado.id);
      const totalXmls = Array.isArray(xmls) ? xmls.length : 0;

      if (totalXmls === 0) {
        dispatch(
          toastrActions.add({
            type: 'error',
            title: 'Erro',
            message: 'Não é possível gerar viagens: o frete não possui XMLs anexados.',
          })
        );
        setModalGerarViagensOpen(false);
        setFreteSelecionado(null);
        setGerandoViagens(false);
        return;
      }

      const resultado = await fretesAPI.gerarViagens(freteSelecionado.id);
      
      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: `${resultado.total_viagens} viagem(ns) gerada(s) com sucesso!`,
        })
      );
      
      setModalGerarViagensOpen(false);
      setFreteSelecionado(null);
      await carregar();
    } catch (e) {
      console.error('[PedidosFrete] Erro ao gerar viagens:', e);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: e.response?.data?.detalhes || e.message || 'Erro ao gerar viagens.',
        })
      );
    } finally {
      setGerandoViagens(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  useEffect(() => {
    if (embarcadores.length > 0) {
      carregarFretes();
    }
  }, [page, itemsPerPage]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div style={{ padding: '20px', width: '100%' }}>
      <Paper
        title="Pedido de Frete"
        icon={Images.IconPaper}
        rightContent={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <IconButton
              onClick={toggleFiltros}
              style={{
                padding: '8px',
                backgroundColor: mostrarFiltros ? '#2196f3' : '#f5f5f5',
                borderRadius: '4px',
                transition: 'all 0.3s'
              }}
              title={mostrarFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            >
              <MdFilterList
                size={24}
                style={{ color: mostrarFiltros ? 'white' : '#122d58' }}
              />
            </IconButton>
          </div>
        }
      >
        {/* Filtros */}
        {mostrarFiltros && (
          <div style={{
            padding: '20px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Filtros</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block'}}>Status</label>
                <select
                  value={filtros.status}
                  onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                  style={{
                    width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd',
                   
                  }}
                >
                  <option value="">Todos</option>
                  <option value="aguardando_cotacao">Aguardando Cotação</option>
                  <option value="aguardando_aprovacao">Aguardando Aprovação</option>
                  <option value="buscando_motorista">Buscando Motorista</option>
                  <option value="aguardando_carregamento">Aguardando Carregamento</option>
                  <option value="emitindo_documentos">Emitindo Documentos</option>
                  <option value="viagem_iniciada">Viagem Iniciada</option>
                  <option value="viagem_encerrada">Viagem Encerrada</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block' }}>Embarcador</label>
                <select
                  value={filtros.embarcador_id}
                  onChange={(e) => setFiltros({ ...filtros, embarcador_id: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">Todos</option>
                  {embarcadores.map((emb) => (
                    <option key={emb.id} value={emb.id}>
                      {emb.nome || emb.nome_fantasia || 'Sem nome'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block' }}>Cidade de Coleta</label>
                <input
                  type="text"
                  value={filtros.cidade_coleta}
                  onChange={(e) => setFiltros({ ...filtros, cidade_coleta: e.target.value })}
                  placeholder="Digite a cidade"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block' }}>Cidade de Entrega</label>
                <input
                  type="text"
                  value={filtros.cidade_entrega}
                  onChange={(e) => setFiltros({ ...filtros, cidade_entrega: e.target.value })}
                  placeholder="Digite a cidade"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block' }}>Com Valor</label>
                <select
                  value={filtros.com_valor}
                  onChange={(e) => setFiltros({ ...filtros, com_valor: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">Todos</option>
                  <option value="sim">Com valor</option>
                  <option value="nao">Sem valor</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: '15px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button label="Limpar Filtros" type="button" onClick={handleLimparFiltros} style={{ backgroundColor: '#999' }} />
              <Button label="Aplicar Filtros" type="button" onClick={handleAplicarFiltros} />
            </div>
          </div>
        )}

        <Container>
          {loading ? (
            <LoadingPage />
          ) : (
            <Table>
              <thead>
                <HeaderRow>
                  <Th>Ações</Th>
                  <Th>Referência</Th>
                  <Th>Status</Th>
                  <Th>Embarcador</Th>
                  <Th>Coleta (Cidade-UF)</Th>
                  <Th>Entrega (Cidade-UF)</Th>
                  <Th>Valor</Th>
                </HeaderRow>
              </thead>
              <tbody>
                {fretes.map((f) => (
                  <Tr key={f.id}>
                    <Td>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, f.id)}
                        style={{ padding: '4px' }}
                      >
                        <MdMoreVert size={20} style={{ color: '#122d58' }} />
                      </IconButton>
                    </Td>
                    <Td>{f.numero ? `FRETE Nº ${f.numero}` : '-'}</Td>
                    <Td>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: getStatusColor(f.status) + '20',
                          color: getStatusColor(f.status),
                          fontSize: '12px',
                          fontWeight: 600,
                          borderRadius: '4px',
                          display: 'block',
                          width: '100%',
                          textAlign: 'center',
                        
                        }}
                      >
                        {getStatusLabel(f.status)}
                      </span>
                    </Td>
                    <Td title={f.embarcador_id}>
                      {getEmbarcadorLabel(embarcadorPorId.get(f.embarcador_id)) || '-'}
                    </Td>
                    <Td>{`${f.collect_city || ''}-${f.collect_state || ''}`.trim()}</Td>
                    <Td>{`${f.delivery_city || ''}-${f.delivery_state || ''}`.trim()}</Td>
                    <Td>
                      {f.valor_frete
                        ? new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(f.valor_frete)
                        : <span style={{ color: '#f44336' }}>Sem valor</span>
                      }
                    </Td>
                  </Tr>
                ))}
                {fretes.length === 0 ? (
                  <Tr>
                    <Td colSpan={7}>Nenhum pedido de frete encontrado.</Td>
                  </Tr>
                ) : null}
              </tbody>
            </Table>
          )}

          {/* Paginação */}
          {!loading && totalItems > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px'
            }}>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Mostrando {Math.min((page - 1) * itemsPerPage + 1, totalItems)} a {Math.min(page * itemsPerPage, totalItems)} de {totalItems} registros
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '14px', color: '#666' }}>Itens por página:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: page === 1 ? '#f5f5f5' : 'white',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    color: page === 1 ? '#ccc' : '#122d58',
                  }}
                >
                  Primeira
                </button>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: page === 1 ? '#f5f5f5' : 'white',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    color: page === 1 ? '#ccc' : '#122d58',
                  }}
                >
                  Anterior
                </button>

                <span style={{
                  padding: '8px 12px',
                  backgroundColor: '#122d58',
                  color: 'white',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}>
                  {page} / {totalPages}
                </span>

                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: page === totalPages ? '#f5f5f5' : 'white',
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                    color: page === totalPages ? '#ccc' : '#122d58',
                  }}
                >
                  Próxima
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: page === totalPages ? '#f5f5f5' : 'white',
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                    color: page === totalPages ? '#ccc' : '#122d58',
                  }}
                >
                  Última
                </button>
              </div>
            </div>
          )}
        </Container>
      </Paper>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => handleAction('view', menuFreteId)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <MdRemoveRedEye size={18} style={{ color: '#122d58' }} />
          Visualizar
        </MenuItem>
        <MenuItem
          onClick={() => handleAction('definir-valor', menuFreteId)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <MdAttachMoney size={18} style={{ color: '#4caf50' }} />
          Definir Valor do Frete
        </MenuItem>
        <MenuItem
          onClick={() => handleAction('associar-motorista', menuFreteId)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <MdLocalShipping size={18} style={{ color: '#2196f3' }} />
          Associar Motorista
        </MenuItem>
        {(['aguardando_carregamento', 'emitindo_documentos'].includes(fretes.find((f) => f.id === menuFreteId)?.status)) && (
          <MenuItem
            onClick={() => handleAction('gerar-viagens', menuFreteId)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <MdDirectionsCar size={18} style={{ color: '#9c27b0' }} />
            Gerar Viagens
          </MenuItem>
        )}
      </Menu>

      <ModalDefinirValor
        open={modalValorOpen}
        onClose={() => {
          setModalValorOpen(false);
          setFreteSelecionado(null);
        }}
        onConfirm={handleDefinirValor}
        frete={freteSelecionado}
      />

      <ModalAssociarMotorista
        open={modalMotoristaOpen}
        onClose={() => {
          setModalMotoristaOpen(false);
          setFreteSelecionado(null);
        }}
        onConfirm={handleAssociarMotorista}
        frete={freteSelecionado}
      />

      <ModalGerarViagens
        open={modalGerarViagensOpen}
        onClose={() => {
          setModalGerarViagensOpen(false);
          setFreteSelecionado(null);
        }}
        onConfirm={handleGerarViagens}
        frete={freteSelecionado}
      />
    </div>
  );
}

